import React, { Fragment } from 'react'
import Head from 'next/head'

import Container from '@dashboard/components/Container'
import ComparisonNav from '@dashboard/components/ComparisonNav'
import Flex from '@dashboard/components/Flex'
import Button from '@dashboard/components/Button'
import ShareActions from '@dashboard/components/ShareActions'
import LoadingBox from '@dashboard/components/LoadingBox'
import ComparisonCard from '@dashboard/components/ComparisonCard'
import PersonalityIcon from '@dashboard/components/PersonalityIcon'
import CelebrityPill from '@dashboard/components/CelebrityPill'

import {
  createImageQueryKey,
  createImageRequest,
} from '@dashboard/store/actions/images/create'
import {
  PublicCelebQueryOptions,
  publicCelebritiesKey,
} from '@dashboard/store/actions/celebrities/public'

import { getDescriptionForGroup as getDescriptionForGroupEnneagram } from '@dashboard/lib/enneagram'
import { APP_URL, MARKETING_APP_URL } from '@dashboard/lib/constants'
import { getDescriptionForGroup as getDescriptionForGroupMyersBriggs } from '@dashboard/lib/myers_briggs'
import { fullName } from '@dashboard/lib/profile'
import { typeDescription } from '@dashboard/lib/personality'
import {
  Profile,
  LoadingStates,
  APIPagedResponse,
  DiscType,
} from '@dashboard/lib/types'
import { NextPageContext } from 'next'

import css from './styles.module.scss'

export interface StateProps {
  getCelebrityStates: Record<string, LoadingStates | undefined>
  getCelebrityResults: Record<string, APIPagedResponse<Profile> | undefined>
  createImageStates: Record<string, LoadingStates | undefined>
  createImageResults: Record<string, string | undefined>
}

export interface DispatchProps {
  loadCelebrities: (options?: PublicCelebQueryOptions) => void
}

export interface OwnProps {
  pathname: string
  personalityFramework: string
  personalityFrameworkKey: string
  typeFromURL: string
  personalityType: string
  asPath: string
}

type Props = StateProps & DispatchProps & OwnProps

class FamousPeopleByPersonalityTypePage extends React.Component<Props> {
  static async getInitialProps({
    query,
    req,
    pathname,
    asPath,
  }: NextPageContext) {
    const personalityFramework = pathname.split('/')[1]
    const typeFromURL = query.personalityType as string
    const personalityType = this.serializePersonalityType(
      String(query.personalityType),
    )
    const isServer = !!req
    const requests: Promise<unknown>[] = []
    const personalityFrameworkKey = personalityFramework.replace('-', '_')

    requests.push(
      createImageRequest(`${APP_URL}${asPath}/header`, 618, 326).catch(
        () => null,
      ),
    )

    if (isServer) await Promise.all(requests)

    return {
      personalityType,
      typeFromURL,
      personalityFramework,
      personalityFrameworkKey,
      pathname,
      asPath,
    }
  }

  /**
   * Serializes the personality type from the query param.
   * Converts infp to INFP, i_d to ID, and id to Id. all
   * enneagram types remain the same
   */
  static serializePersonalityType(type: string) {
    if (/[1-9]/.test(type)) {
      return type
    } else if ([1, 3, 4].includes(type.length)) {
      return type.replace('-', '').toUpperCase()
    } else {
      return `${type.charAt(0).toUpperCase()}${type.charAt(1).toLowerCase()}`
    }
  }

  componentDidMount() {
    const { loadCelebrities, personalityFrameworkKey, personalityType } =
      this.props

    loadCelebrities({
      [personalityFrameworkKey]: personalityType,
    })
  }

  componentDidUpdate(prevProps: Props) {
    const {
      loadCelebrities,
      personalityFrameworkKey,
      personalityType,
      getCelebrityStates,
    } = this.props
    const key = publicCelebritiesKey({
      [personalityFrameworkKey]: personalityType,
    })
    const celebrityState = getCelebrityStates[key] || 'default'
    const shouldLoadCelebs =
      celebrityState === 'default' &&
      prevProps.personalityType !== personalityType

    if (shouldLoadCelebs) {
      loadCelebrities({
        [personalityFrameworkKey]: personalityType,
      })
    }
  }

  render() {
    return (
      <div className={css.container}>
        <Head>{this.renderOGTags()}</Head>
        <div className={css.background} />
        {this.renderContent()}
      </div>
    )
  }

  // Partials
  renderOGTags() {
    const typeTitle = this.personalityTypeTitle()
    const title = `Famous People with the ${typeTitle} Personality Type`
    const description = `See examples of famous people and celebrities who have the ${typeTitle} personality type.`
    const ogImage = this.ogImageResult()

    return (
      <Fragment>
        <title>{title}</title>
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta name="description" content={description}></meta>
        <meta
          property="og:image"
          content={
            ogImage ||
            'https://www.crystalknows.com/hubfs/WebsiteApril2019/og-image.png'
          }
        />
        <meta property="og:image:width" content="618" />
        <meta property="og:image:height" content="326" />
      </Fragment>
    )
  }

  renderContent() {
    return (
      <Container>
        <ComparisonNav showLogin />
        {this.renderTypeIcon()}
        <h1
          className={css.pageTitle}
        >{`Famous People with the ${this.personalityTypeTitle()} Personality Type`}</h1>
        <div className={css.divider} />
        {this.renderTypeDescription()}
        {this.renderCelebritiesCard()}
      </Container>
    )
  }

  renderTypeDescription() {
    const { personalityFramework, typeFromURL, personalityType } = this.props
    let description = ''
    let href = ''

    if (personalityFramework === 'disc') {
      description = typeDescription(personalityType as DiscType)
      href = `${MARKETING_APP_URL}/disc/${typeFromURL}-personality-type`
    } else if (personalityFramework === 'enneagram') {
      description = getDescriptionForGroupEnneagram(personalityType)
      href = `${MARKETING_APP_URL}/enneagram/type-${typeFromURL}`
    } else {
      description = getDescriptionForGroupMyersBriggs(personalityType)
      href = `${MARKETING_APP_URL}/myers-briggs/${typeFromURL}`
    }

    const match = description.match(/(<.*>)/)
    const type = match && match[0]

    if (!type) return null

    const brokenDownDescription = description.split(type, 2)

    return (
      <p className={css.typeDescription}>
        {`${brokenDownDescription[0]} `}
        <a
          target="_blank"
          rel="noopener noreferrer"
          className={css.typeEmphasis}
          href={href}
        >
          {type.replace(/<|>/g, '')}
        </a>
        {` ${brokenDownDescription[1]}`}
      </p>
    )
  }

  renderBodyFailed() {
    return (
      <Flex vertical alignCenter>
        <p className={css.text}>There was a problem loading this page.</p>
        <Button onClick={() => window.location.reload()}>Reload</Button>
      </Flex>
    )
  }

  renderTypeIcon() {
    const { personalityType } = this.props

    return (
      <Flex fullWidth justifyCenter>
        <PersonalityIcon
          personality={personalityType}
          size={110}
          padding={16}
        />
      </Flex>
    )
  }

  renderCelebritiesCard() {
    return (
      <div className={css.celebritiesCardContainer}>
        <ComparisonCard noMargin>
          <Flex responsiveVertical>
            {this.renderShareActions()}
            {this.renderCelebritiesContent()}
          </Flex>
        </ComparisonCard>
      </div>
    )
  }

  renderCelebritiesContent() {
    const isLoading = ['default', 'loading'].includes(this.celebritiesState())
    const failed = ['failed'].includes(this.celebritiesState())
    const celebrities = this.celebritiesResult()

    if (isLoading) return <LoadingBox />
    else if (failed) return this.renderBodyFailed()
    else if (celebrities.length === 0) return this.renderEmptyState()
    else
      return (
        <div className={css.celebrities}>
          {celebrities.map(this.renderCelebrity)}
        </div>
      )
  }

  renderEmptyState() {
    return (
      <div className={css.celebrities}>
        <p className={css.text}>
          There was a problem finding famous people with this personality type.
        </p>
      </div>
    )
  }

  renderCelebrity = (celeb: Profile) => {
    const { personalityType } = this.props

    return (
      <div className={css.celebPillContainer} key={celeb.id}>
        <CelebrityPill celebrity={celeb} personalityType={personalityType} />
      </div>
    )
  }

  renderShareActions() {
    const celebrities = this.celebritiesResult()
    let text = `See famous ${this.personalityTypeTitle()} personalities at <url> via @crystalknowsme`
    const splitPoint = ' at <url> '

    if (celebrities.length) {
      const broken = text.split(splitPoint)
      broken[0] = `${broken[0]}, like ${celebrities
        .slice(0, 2)
        .map(fullName)
        .join(' and ')}`
      text = broken.join(splitPoint)
    }

    return (
      <div className={css.shareActionsContainer}>
        <ShareActions responsiveHorizontal tweet={{ text }} />
      </div>
    )
  }

  // Functions
  celebritiesKey() {
    const { personalityFrameworkKey, personalityType } = this.props

    return publicCelebritiesKey({
      [personalityFrameworkKey]: personalityType,
    })
  }

  celebritiesResult() {
    const { getCelebrityResults } = this.props
    const results = getCelebrityResults[this.celebritiesKey()]

    return results?.data || []
  }

  celebritiesState() {
    const { getCelebrityStates } = this.props

    return getCelebrityStates[this.celebritiesKey()] || 'default'
  }

  ogImageKey() {
    const { asPath } = this.props

    return createImageQueryKey(`${APP_URL}${asPath}/header`, 618, 326)
  }

  ogImageResult() {
    const { createImageResults } = this.props

    return createImageResults[this.ogImageKey()] || null
  }

  ogImageState() {
    const { createImageStates } = this.props

    return createImageStates[this.ogImageKey()] || 'default'
  }

  personalityTypeTitle() {
    const { personalityType } = this.props

    if (/[1-9]/.test(personalityType)) return `Enneagram ${personalityType}`
    else if (personalityType.length === 4) return personalityType
    else return `DISC ${personalityType}`
  }
}

export default FamousPeopleByPersonalityTypePage
